import { ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { first } from 'rxjs/operators';
import swal from 'sweetalert2';

import { AuthService } from '../../auth/AuthService';
import { InsuranceService } from '../../Services/insurance.service';

type ReviewStatus = 'PENDING' | 'PASS' | 'FAIL' | 'NOT_APPLICABLE';
type AuditStatus = 'PENDING' | 'IN_AUDIT' | 'CHANGES_REQUESTED' | 'PASSED' | 'PUBLISHED' | 'FAILED';

interface AuditQuestion {
  question_id: number;
  category_type: string;
  section: string;
  question: string;
  question_code: string;
  severity: string;
  category_weight: number;
  question_weight: number;
  deduction_percent: number | null;
  failure_count: number | null;
  sequence: number;
  field_path: string;
  answer: any;
  answer_matched: boolean;
  review_status: ReviewStatus;
  error_category: string | null;
  error_type: string | null;
  other_error_detail_required: boolean;
  other_error_detail: string | null;
  remark: string | null;
  expected_value: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;

  draftStatus: ReviewStatus;
  draftRemark: string;
  draftExpectedValue: string;
  draftOtherErrorDetail: string;
  dirty: boolean;
}

interface AuditProgress {
  total: number;
  reviewed: number;
  passed: number;
  failed: number;
  notApplicable: number;
  complete: boolean;
}

@Component({
  selector: 'pm-audit-review',
  templateUrl: './audit-review.component.html',
  styleUrls: ['./audit-review.component.css']
})
export class AuditReviewComponent implements OnInit {
  auditId = 0;
  currentVersion = 0;
  revisionId = 0;
  status: AuditStatus = 'PENDING';

  loading = true;
  saving = false;
  workflowLoading = false;

  audit: any = {};
  snapshot: any = {};
  questions: AuditQuestion[] = [];
  findings: any[] = [];
  timeline: any[] = [];
  revisions: any[] = [];

  progress: AuditProgress = {
    total: 0,
    reviewed: 0,
    passed: 0,
    failed: 0,
    notApplicable: 0,
    complete: false
  };

  score: number | null = null;
  passingScore = 95;
  criticalFailures = 0;
  passEligible = false;
  severityFailureCounts: { [severity: string]: number } = {};
  severityFailureLimits: { [severity: string]: number } = {};
  automaticFailureSeverities: string[] = [];
  automaticFailure = false;

  readonly auditTypeOptions = ['BREAKDOWN', 'PMS', 'BOTH'];
  readonly validationSourceOptions = ['FAXBACK', 'CALLER_ID', 'WEB'];
  auditTypes: string[] = [];
  validationSources: string[] = [];
  faxbackDocument: any = null;
  selectedFaxback: File | null = null;
  uploadedFaxbackBatchId: string | null = null;
  uploadedFaxbackFileId: number | null = null;
  metadataDirty = false;
  viewingFaxback = false;

  activeSection = 'ALL';
  selectedQuestion: AuditQuestion | null = null;

  readonly sectionLabels: { [key: string]: string } = {
    ALL: 'All Questions',
    PERSONAL: 'Patient Information',
    MAX_DEDUCTIBLE: 'Max & Deductible',
    CO_INSURANCE: 'Co-Insurance',
    COVERAGE: 'Coverage & Frequency',
    ORTHO: 'Ortho',
    HISTORY: 'History'
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private insuranceService: InsuranceService,
    public authService: AuthService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.auditId = Number(this.route.snapshot.paramMap.get('id') || 0);

    if (!this.auditId) {
      this.router.navigate(['/audit/queue']);
      return;
    }

    this.loadAudit();
  }

  loadAudit(): void {
    const clientId = this.authService.getClientId();
    if (!clientId) {
      return;
    }

    this.loading = true;

    forkJoin([
      this.insuranceService.getAuditDetail(this.auditId, { clientId }),
      this.insuranceService.getAuditQuestions(this.auditId, { clientId })
    ]).pipe(first()).subscribe(
      ([response, questions]: [any, any]) => {
        this.ngZone.run(() => {
          this.applyAuditResponse({
            ...response,
            questions: Array.isArray(questions) ? questions : []
          });
          this.loading = false;
          this.cdr.detectChanges();
        });

        this.startPendingAuditIfAllowed();
      },
      (error: any) => {
        this.ngZone.run(() => {
          this.loading = false;
          this.cdr.detectChanges();
        });

        this.showApiError('Unable to Load Audit', error);
      }
    );
  }

  private applyAuditResponse(response: any): void {
    if (!response) {
      return;
    }

    this.audit = { ...this.audit, ...response };
    if (Array.isArray(response.audit_types) || Array.isArray(response.auditTypes)) {
      this.auditTypes = (response.audit_types || response.auditTypes).slice();
    }
    if (Array.isArray(response.validation_sources) || Array.isArray(response.validationSources)) {
      this.validationSources = (response.validation_sources || response.validationSources).slice();
    }
    if (response.faxback_document !== undefined) {
      this.faxbackDocument = response.faxback_document;
      this.uploadedFaxbackBatchId = null;
      this.uploadedFaxbackFileId = null;
    }
    this.currentVersion = Number(response.version ?? this.currentVersion ?? 0);
    this.revisionId = Number(response.revision_id ?? this.revisionId ?? 0);
    this.status = (response.status || this.status || 'PENDING') as AuditStatus;
    this.snapshot = response.snapshot ?? this.snapshot ?? {};
    this.findings = Array.isArray(response.findings) ? response.findings : this.findings;

    const incomingQuestions = Array.isArray(response.questions) ? response.questions : [];
    if (incomingQuestions.length) {
      this.questions = incomingQuestions
        .map((question: any) => this.mapQuestion(question))
        .sort((a: AuditQuestion, b: AuditQuestion) => a.sequence - b.sequence);

      if (this.selectedQuestion) {
        const refreshed = this.questions.find(
          question => question.question_id === this.selectedQuestion!.question_id
        );
        this.selectedQuestion = refreshed || this.questions[0] || null;
      } else {
        this.selectedQuestion = this.questions[0] || null;
      }
    }

    const progress = response.question_progress ?? {};
    const total = Number(progress.total ?? this.questions.length ?? 0);
    const reviewed = Number(progress.reviewed ?? 0);

    this.progress = {
      total,
      reviewed,
      passed: Number(progress.passed ?? 0),
      failed: Number(progress.failed ?? 0),
      notApplicable: Number(progress.notApplicable ?? progress.not_applicable ?? 0),
      complete:
        progress.complete !== null && progress.complete !== undefined
          ? Boolean(progress.complete)
          : total > 0 && reviewed >= total
    };

    this.score =
      response.score === null || response.score === undefined
        ? null
        : Number(response.score);
    this.passingScore = Number(response.passing_score ?? 95);
    this.criticalFailures = Number(response.critical_failures ?? 0);
    this.passEligible = Boolean(response.pass_eligible);
    this.applySeverityFailureState(response);
  }

  private mapQuestion(question: any): AuditQuestion {
    const status = (question.review_status || 'PENDING') as ReviewStatus;

    return {
      ...question,
      question_id: Number(question.question_id),
      category_weight: Number(question.category_weight ?? 0),
      question_weight: Number(question.question_weight ?? 0),
      deduction_percent:
        question.deduction_percent === null || question.deduction_percent === undefined
          ? null
          : Number(question.deduction_percent),
      failure_count:
        question.failure_count === null || question.failure_count === undefined
          ? null
          : Number(question.failure_count),
      sequence: Number(question.sequence ?? 0),
      review_status: status,
      error_category: question.error_category || null,
      error_type: question.error_type || null,
      other_error_detail_required: Boolean(question.other_error_detail_required),
      other_error_detail: question.other_error_detail || null,
      draftStatus: status,
      draftRemark: question.remark || '',
      draftExpectedValue: question.expected_value || '',
      draftOtherErrorDetail: question.other_error_detail || '',
      dirty: false
    };
  }

  private applySeverityFailureState(response: any): void {
    if (!response) {
      return;
    }

    if (response.severity_failure_counts && typeof response.severity_failure_counts === 'object') {
      this.severityFailureCounts = { ...response.severity_failure_counts };
    }

    if (response.severity_failure_limits && typeof response.severity_failure_limits === 'object') {
      this.severityFailureLimits = { ...response.severity_failure_limits };
    }

    if (Array.isArray(response.automatic_failure_severities)) {
      this.automaticFailureSeverities = response.automatic_failure_severities.slice();
    }

    if (response.automatic_failure !== undefined && response.automatic_failure !== null) {
      this.automaticFailure = Boolean(response.automatic_failure);
    }

    if (response.pass_eligible !== undefined) {
      this.passEligible = Boolean(response.pass_eligible);
    }
  }

  private startPendingAuditIfAllowed(): void {
    if (
      this.status !== 'PENDING' ||
      !this.authService.hasRole('audit-edit') ||
      this.workflowLoading
    ) {
      return;
    }

    // The backend is authoritative here. For an unassigned PENDING audit,
    // POST /start assigns the logged-in Keycloak user and increments version.
    // If it is assigned to someone else, the backend returns 403.
    this.workflowLoading = true;

    this.insuranceService.startAudit(
      this.auditId,
      { version: this.currentVersion },
      { clientId: this.authService.getClientId() }
    ).pipe(first()).subscribe(
      (response: any) => {
        this.ngZone.run(() => {
          this.applyAuditResponse(response);
          this.workflowLoading = false;
          this.cdr.detectChanges();
        });
      },
      (error: any) => {
        this.ngZone.run(() => {
          this.workflowLoading = false;
          this.cdr.detectChanges();
        });

        this.showApiError('Unable to Start Audit', error);
      }
    );
  }

  toggleAuditType(value: string, checked: boolean): void {
    if (!this.canEdit) return;
    if (checked) {
      this.auditTypes = value === 'BOTH' ? ['BOTH'] :
        [...this.auditTypes.filter(v => v !== 'BOTH' && v !== value), value];
    } else {
      this.auditTypes = this.auditTypes.filter(v => v !== value);
    }
    // The API accepts BOTH as the combined selection.
    if (this.auditTypes.includes('BREAKDOWN') && this.auditTypes.includes('PMS')) {
      this.auditTypes = ['BOTH'];
    }
    this.metadataDirty = true;
  }

  toggleValidationSource(value: string, checked: boolean): void {
    if (!this.canEdit) return;
    this.validationSources = checked
      ? [...this.validationSources.filter(v => v !== value), value]
      : this.validationSources.filter(v => v !== value);
    this.metadataDirty = true;
  }

  chooseFaxback(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file || !this.canEdit) return;
    if (file.size > 20 * 1024 * 1024) {
      swal.fire({ icon: 'warning', title: 'File Too Large', text: 'Select a file no larger than 20 MB.' });
      input.value = '';
      return;
    }
    this.selectedFaxback = file;
    if (!this.validationSources.includes('FAXBACK')) {
      this.validationSources = [...this.validationSources, 'FAXBACK'];
    }
    this.uploadedFaxbackBatchId = null;
    this.uploadedFaxbackFileId = null;
    this.metadataDirty = true;
    input.value = '';
  }

  clearSelectedFaxback(): void {
    this.selectedFaxback = null;
    this.uploadedFaxbackBatchId = null;
    this.uploadedFaxbackFileId = null;
    this.metadataDirty = true;
  }

  async openFaxback(): Promise<void> {
    const doc = this.faxbackDocument;
    if (!doc || this.viewingFaxback) return;
    const batchId = doc.batchId || doc.batch_id;
    const fileId = Number(doc.fileId || doc.file_id);
    const clientId = this.authService.getClientId();
    if (!batchId || !fileId || !clientId) return;
    // Open synchronously to avoid popup blockers after the asynchronous download.
    const tab = window.open('', '_blank');
    this.viewingFaxback = true;
    try {
      const blob = await this.insuranceService.getAuditFaxbackContent(batchId, fileId, clientId)
        .pipe(first()).toPromise();
      const objectUrl = URL.createObjectURL(blob);
      if (tab) {
        tab.location.href = objectUrl;
        // Delay revocation so browser PDF viewers can load the document.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      } else {
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = doc.fileName || 'faxback';
        anchor.click();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      }
    } catch (error) {
      if (tab) tab.close();
      this.showApiError('Unable to Open Faxback', error);
    } finally {
      this.viewingFaxback = false;
    }
  }

  private async saveReviewMetadata(): Promise<boolean> {
    if (!this.metadataDirty && !this.selectedFaxback) return true;
    const clientId = this.authService.getClientId();
    if (!clientId) return false;
    this.saving = true;
    try {
      let batchId = this.uploadedFaxbackBatchId ||
        (this.faxbackDocument && this.faxbackDocument.batchId) || null;
      let fileId = this.uploadedFaxbackFileId ||
        (this.faxbackDocument && this.faxbackDocument.fileId) || null;

      if (this.selectedFaxback && !this.uploadedFaxbackFileId) {
        const date = new Date().toISOString().slice(0, 10);
        const batch = await this.insuranceService.createAuditStorageBatch({
          clientId, name: `Audit ${this.auditId} Faxback`, serviceType: 'AUDIT', businessDate: date
        }).pipe(first()).toPromise();
        batchId = batch && batch.id;
        if (!batchId) throw new Error('Storage batch ID was not returned.');
        const upload = await this.insuranceService.uploadAuditFaxback(batchId, clientId, this.selectedFaxback)
          .pipe(first()).toPromise();
        fileId = upload && upload.files && upload.files[0] && upload.files[0].id;
        if (!fileId) throw new Error('Uploaded file ID was not returned.');
        this.uploadedFaxbackBatchId = batchId;
        this.uploadedFaxbackFileId = Number(fileId);
      }

      const response = await this.insuranceService.saveAuditReviewMetadata(this.auditId, {
        version: this.currentVersion,
        auditTypes: this.auditTypes.slice(),
        validationSources: this.validationSources.slice(),
        faxbackBatchId: batchId,
        faxbackFileId: fileId
      }, { clientId }).pipe(first()).toPromise();
      this.currentVersion = Number(response && response.version !== undefined
        ? response.version : this.currentVersion);
      this.audit = { ...this.audit, ...response };
      if (response && response.score !== undefined && response.score !== null) {
        this.score = Number(response.score);
      }
      this.applySeverityFailureState(response);
      this.metadataDirty = false;
      this.selectedFaxback = null;
      this.uploadedFaxbackBatchId = null;
      this.uploadedFaxbackFileId = null;
      // GET is authoritative for the document link and revised version.
      const refreshed = await this.insuranceService.getAuditDetail(this.auditId, { clientId })
        .pipe(first()).toPromise();
      if (refreshed) {
        this.currentVersion = Number(refreshed.version ?? this.currentVersion);
        this.audit = { ...this.audit, ...refreshed };
        this.faxbackDocument = refreshed.faxback_document || null;
        if (Array.isArray(refreshed.audit_types)) this.auditTypes = refreshed.audit_types.slice();
        if (Array.isArray(refreshed.validation_sources)) this.validationSources = refreshed.validation_sources.slice();
      }
      return true;
    } catch (error) {
      this.showApiError('Unable to Save Audit Metadata', error);
      return false;
    } finally {
      this.saving = false;
      this.cdr.detectChanges();
    }
  }

  get canEdit(): boolean {
    return this.authService.hasRole('audit-edit') &&
      (this.status === 'PENDING' || this.status === 'IN_AUDIT');
  }

  get canPassAudit(): boolean {
    // Use the saved score rather than pass_eligible, which can lag behind review updates.
    return this.canEdit &&
      !this.saving &&
      !this.workflowLoading &&
      this.progress.complete &&
      this.score !== null &&
      Number.isFinite(this.score) &&
      this.score >= 95 &&
      !this.automaticFailure;
  }

  get canRequestChanges(): boolean {
    return this.canEdit && !this.saving && !this.workflowLoading &&
      this.progress.complete && this.score !== null &&
      Number.isFinite(this.score) && this.score < 100;
  }

  get loggedInUserId(): string | null {
    return this.authService.getAuthContext()?.userProfile?.userId || null;
  }

  get sections(): string[] {
    const sections = Array.from(
      new Set(this.questions.map(question => question.section).filter(Boolean))
    );
    return ['ALL', ...sections];
  }

  get visibleQuestions(): AuditQuestion[] {
    if (this.activeSection === 'ALL') {
      return this.questions;
    }

    return this.questions.filter(question => question.section === this.activeSection);
  }

  selectSection(section: string): void {
    this.activeSection = section;
    const first = this.visibleQuestions[0];
    if (first) {
      this.selectedQuestion = first;
    }
  }

  selectQuestion(question: AuditQuestion): void {
    this.selectedQuestion = question;
  }

  updateQuestionDraft(question: AuditQuestion): void {
    if (question.draftStatus !== 'FAIL') {
      question.draftExpectedValue = '';
      question.draftOtherErrorDetail = '';
    } else if (!question.other_error_detail_required) {
      question.draftOtherErrorDetail = '';
    }

    question.dirty = this.isQuestionDirty(question);
  }

  markQuestionStatus(question: AuditQuestion, status: ReviewStatus): void {
    if (!this.canEdit || status === 'PENDING') {
      return;
    }

    question.draftStatus = status;

    if (status === 'PASS') {
      question.draftExpectedValue = '';
      // PASS may still carry an optional auditor remark.
    }

    if (status === 'NOT_APPLICABLE') {
      question.draftExpectedValue = '';
    }

    this.updateQuestionDraft(question);
  }

  private isQuestionDirty(question: AuditQuestion): boolean {
    return question.draftStatus !== question.review_status ||
      (question.draftRemark || '') !== (question.remark || '') ||
      (question.draftExpectedValue || '') !== (question.expected_value || '') ||
      (question.draftOtherErrorDetail || '') !== (question.other_error_detail || '');
  }

  private validateQuestion(question: AuditQuestion): string | null {
    if (question.draftStatus === 'PENDING') {
      return 'Select PASS, FAIL, or NOT APPLICABLE.';
    }

    if (question.draftStatus === 'FAIL') {
      if (!question.draftRemark.trim()) {
        return 'Remark is required for a failed question.';
      }

      if (question.other_error_detail_required && !question.draftOtherErrorDetail.trim()) {
        return 'Other Error Detail is required for this Error Type.';
      }
    }

    if (
      question.draftStatus === 'NOT_APPLICABLE' &&
      !question.draftRemark.trim()
    ) {
      return 'Remark is required when a question is Not Applicable.';
    }

    return null;
  }

  saveAudit(): void {
    this.savePendingQuestions(true);
  }

  private async savePendingQuestions(showSuccess: boolean): Promise<boolean> {
    if (!this.canEdit) {
      return false;
    }

    const changedQuestions = this.questions
      .filter(question => question.dirty)
      .sort((a, b) => a.sequence - b.sequence);

    if (!changedQuestions.length) {
      if (this.metadataDirty || this.selectedFaxback) {
        const saved = await this.saveReviewMetadata();
        if (saved && showSuccess) swal.fire({ icon: 'success', title: 'Audit Saved', text: 'Audit metadata saved successfully.' });
        return saved;
      }
      if (showSuccess) {
        swal.fire({
          icon: 'info',
          title: 'No Changes',
          text: 'There are no changed audit questions to save.'
        });
      }
      return true;
    }

    for (const question of changedQuestions) {
      const validationError = this.validateQuestion(question);
      if (validationError) {
        this.selectedQuestion = question;
        swal.fire({
          icon: 'warning',
          title: question.question,
          text: validationError
        });
        return false;
      }
    }

    this.saving = true;

    try {
      let lastResponse: any = null;

      for (const question of changedQuestions) {
        const response = await this.insuranceService.reviewAuditQuestion(
          this.auditId,
          question.question_id,
          this.buildQuestionPayload(question),
          { clientId: this.authService.getClientId() }
        ).pipe(first()).toPromise();

        lastResponse = response;
        this.currentVersion = Number(response?.version ?? this.currentVersion);
        this.audit = { ...this.audit, ...response };
        this.score =
          response?.score === null || response?.score === undefined
            ? this.score
            : Number(response.score);
        this.passEligible =
          response?.pass_eligible === undefined
            ? this.passEligible
            : Boolean(response.pass_eligible);
        this.criticalFailures = Number(
          response?.critical_failures ?? this.criticalFailures
        );
        this.applySeverityFailureState(response);
        this.findings = Array.isArray(response?.findings)
          ? response.findings
          : this.findings;
        const responseProgress = response?.question_progress ?? response?.progress;
        if (responseProgress) {
          const total = Number(responseProgress.total ?? 0);
          const reviewed = Number(responseProgress.reviewed ?? 0);

          this.progress = {
            total,
            reviewed,
            passed: Number(responseProgress.passed ?? 0),
            failed: Number(responseProgress.failed ?? 0),
            notApplicable: Number(
              responseProgress.notApplicable ??
              responseProgress.not_applicable ??
              0
            ),
            complete:
              responseProgress.complete !== null &&
              responseProgress.complete !== undefined
                ? Boolean(responseProgress.complete)
                : total > 0 && reviewed >= total
          };
        }

        question.review_status = question.draftStatus;
        question.remark = question.draftRemark || null;
        question.expected_value = question.draftExpectedValue || null;
        question.other_error_detail = question.draftOtherErrorDetail || null;
        question.dirty = false;
      }

      if (lastResponse) {
        this.applyAuditResponse(lastResponse);
      }

      this.ngZone.run(() => {
        this.saving = false;
        this.cdr.detectChanges();
      });

      if (!(await this.saveReviewMetadata())) return false;
      if (showSuccess) {
        swal.fire({
          icon: 'success',
          title: 'Audit Saved',
          text: `${changedQuestions.length} question${changedQuestions.length === 1 ? '' : 's'} saved successfully.`,
          showConfirmButton: false,
          timer: 1800
        });
      }

      return true;
    } catch (error) {
      this.ngZone.run(() => {
        this.saving = false;
        this.cdr.detectChanges();
      });

      this.showApiError('Unable to Save Audit', error);
      return false;
    }
  }

  private buildQuestionPayload(question: AuditQuestion): any {
    return {
      version: this.currentVersion,
      status: question.draftStatus,
      remark: question.draftRemark?.trim() || null,
      expectedValue: question.draftStatus === 'FAIL'
        ? (question.draftExpectedValue?.trim() || null)
        : null,
      otherErrorDetail: question.draftStatus === 'FAIL' && question.other_error_detail_required
        ? (question.draftOtherErrorDetail?.trim() || null)
        : null
    };
  }

  async resolveFinding(finding: any): Promise<void> {
    if (!this.canEdit) {
      return;
    }

    const questionId = Number(
      finding?.question_id ?? finding?.questionId
    );
    const question = this.questions.find(
      item => item.question_id === questionId
    );

    if (!question) {
      swal.fire({
        icon: 'error',
        title: 'Finding Not Linked',
        text: 'The question linked to this finding could not be found.'
      });
      return;
    }

    try {
      const response = await this.insuranceService.reviewAuditQuestion(
        this.auditId,
        question.question_id,
        {
          version: this.currentVersion,
          status: 'PASS',
          remark: null,
          expectedValue: null,
          otherErrorDetail: null
        },
        { clientId: this.authService.getClientId() }
      ).pipe(first()).toPromise();

      this.applyAuditResponse(response);

      swal.fire({
        icon: 'success',
        title: 'Finding Resolved',
        text: 'The question has been marked as passed.',
        showConfirmButton: false,
        timer: 1600
      });
    } catch (error) {
      this.showApiError('Unable to Resolve Finding', error);
    }
  }

  editFinding(finding: any): void {
    const questionId = Number(
      finding?.question_id ?? finding?.questionId
    );
    const question = this.questions.find(
      item => item.question_id === questionId
    );

    if (!question) {
      return;
    }

    this.activeSection = question.section;
    this.selectedQuestion = question;
    question.draftStatus = 'FAIL';
    question.draftRemark = finding?.remark || question.remark || '';
    question.draftExpectedValue =
      finding?.expected_value || question.expected_value || '';
    question.draftOtherErrorDetail =
      finding?.other_error_detail || question.other_error_detail || '';
    question.dirty = true;
  }

  async requestChanges(): Promise<void> {
    if (!this.canEdit) {
      return;
    }

    const saved = await this.savePendingQuestions(false);
    if (!saved) {
      return;
    }

    if (!this.progress.complete) {
      swal.fire({
        icon: 'warning',
        title: 'Audit Incomplete',
        text: 'Review every question before requesting changes.'
      });
      return;
    }

    if (this.score === null || !Number.isFinite(this.score) || this.score >= 100) {
      swal.fire({ icon: 'info', title: 'Request Changes Unavailable',
        text: 'Changes can be requested only when the saved audit score is below 100%.' });
      return;
    }

    this.workflowLoading = true;

    try {
      const response = await this.insuranceService.requestAuditChanges(
        this.auditId,
        { version: this.currentVersion },
        { clientId: this.authService.getClientId() }
      ).pipe(first()).toPromise();

      this.applyAuditResponse(response);
      this.workflowLoading = false;

      await swal.fire({
        icon: 'success',
        title: 'Changes Requested',
        text: 'The audit has been returned for correction.'
      });

      this.router.navigate(['/audit/queue']);
    } catch (error) {
      this.workflowLoading = false;
      this.showApiError('Unable to Request Changes', error);
    }
  }

  async passAudit(): Promise<void> {
    if (!this.canEdit) {
      return;
    }

    const saved = await this.savePendingQuestions(false);
    if (!saved) {
      return;
    }

    if (!this.progress.complete) {
      swal.fire({
        icon: 'warning',
        title: 'Audit Incomplete',
        text: 'Review every question before passing the audit.'
      });
      return;
    }

    if (this.score === null || !Number.isFinite(this.score) || this.score < 95) {
      swal.fire({
        icon: 'warning',
        title: 'Audit Score Below 95%',
        text: 'A saved audit score of at least 95% is required to pass.'
      });
      return;
    }

    if (this.automaticFailure) {
      swal.fire({
        icon: 'warning',
        title: 'Automatic Failure Triggered',
        text: 'Resolve the automatic severity failure before passing the audit.'
      });
      return;
    }

    this.workflowLoading = true;

    try {
      const response = await this.insuranceService.passAudit(
        this.auditId,
        { version: this.currentVersion },
        { clientId: this.authService.getClientId() }
      ).pipe(first()).toPromise();

      this.applyAuditResponse(response);
      this.workflowLoading = false;

      if (response?.status === 'FAILED' || response?.automatic_failure === true) {
        const severities = Array.isArray(response?.automatic_failure_severities)
          ? response.automatic_failure_severities.join(', ')
          : this.automaticFailureSeverities.join(', ');

        await swal.fire({
          icon: 'error',
          title: 'Audit Failed',
          text: severities
            ? `Automatic failure threshold reached for: ${severities}.`
            : 'An automatic severity failure threshold has been reached.'
        });

        this.router.navigate(['/audit/queue']);
        return;
      }

      await swal.fire({
        icon: 'success',
        title: 'Audit Passed',
        text: 'The audit has been completed successfully.'
      });

      this.router.navigate(['/audit/finalization', this.auditId]);
    } catch (error) {
      this.workflowLoading = false;
      this.showApiError('Unable to Pass Audit', error);
    }
  }

  loadTimeline(): void {
    this.insuranceService.getAuditTimeline(
      this.auditId,
      { clientId: this.authService.getClientId() }
    ).pipe(first()).subscribe(
      (response: any[]) => {
        this.timeline = Array.isArray(response) ? response : [];
      },
      (error: any) => this.showApiError('Unable to Load Activity', error)
    );
  }

  loadRevisions(): void {
    this.insuranceService.getAuditRevisions(
      this.auditId,
      { clientId: this.authService.getClientId() }
    ).pipe(first()).subscribe(
      (response: any[]) => {
        this.revisions = Array.isArray(response) ? response : [];
      },
      (error: any) => this.showApiError('Unable to Load Revisions', error)
    );
  }

  goBack(): void {
    this.router.navigate(['/audit/queue']);
  }

  get patientName(): string {
    return this.audit?.patient_name ||
      [
        this.snapshot?.PERSONAL?.registration?.firstName,
        this.snapshot?.PERSONAL?.registration?.lastName
      ].filter(Boolean).join(' ') ||
      'Patient';
  }

  get patientDob(): string {
    return this.snapshot?.PERSONAL?.registration?.dob || '-';
  }

  get clientName(): string {
    return this.snapshot?.PERSONAL?.registration?.clientName ||
      this.snapshot?.PERSONAL?.registration?.client?.name ||
      '-';
  }

  get observedAnswer(): string {
    if (!this.selectedQuestion) {
      return '-';
    }

    return this.getAnswerSummary(this.selectedQuestion);
  }

  get observedAnswerFields(): Array<{ key: string; label: string; value: string }> {
    if (!this.selectedQuestion) {
      return [];
    }

    return this.getAnswerFields(this.selectedQuestion.answer);
  }

  getAnswerSummary(question: AuditQuestion | any): string {
    const answer = question?.answer;

    if (answer === null || answer === undefined || answer === '') {
      return '-';
    }

    if (Array.isArray(answer)) {
      const values = answer
        .map(item => this.formatScalarAnswer(item))
        .filter(value => value !== '-');

      return values.length ? values.join(', ') : '-';
    }

    if (typeof answer !== 'object') {
      return this.formatScalarAnswer(answer);
    }

    const fields = this.getAnswerFields(answer);
    if (!fields.length) {
      return '-';
    }

    if (fields.length === 1) {
      return fields[0].value;
    }

    return fields
      .map(field => `${field.label}: ${field.value}`)
      .join(' · ');
  }

  private getAnswerFields(answer: any): Array<{ key: string; label: string; value: string }> {
    if (!answer || typeof answer !== 'object' || Array.isArray(answer)) {
      return [];
    }

    const metadataKeys = new Set([
      'id',
      'active',
      'patientId',
      'patient_id',
      'planInfo',
      'serviceName',
      'serviceType',
      'procedureCode',
      'questionCode',
      'network',
      'networkType',
      'network_type',
      'planType',
      'plan_type',
      'createdAt',
      'created_at',
      'updatedAt',
      'updated_at',
      'createdBy',
      'created_by',
      'updatedBy',
      'updated_by'
    ]);

    const preferredOrder = [
      'response',
      'amount',
      'percentage',
      'percent',
      'frequency',
      'guideline',
      'guidelines',
      'limitation',
      'age',
      'ageLowValue',
      'ageHighValue',
      'age_low_value',
      'age_high_value',
      'waitingPeriod',
      'waiting_period',
      'deductibleApplies',
      'deductible_applies',
      'maximumApplies',
      'maximum_applies',
      'disclaimers',
      'coverage',
      'eligible',
      'status',
      'type'
    ];

    const keys = Object.keys(answer).filter(key => !metadataKeys.has(key));
    const orderedKeys = [
      ...preferredOrder.filter(key => keys.includes(key)),
      ...keys.filter(key => !preferredOrder.includes(key))
    ];

    return orderedKeys.map(key => ({
      key,
      label: this.answerFieldLabel(key),
      value: this.formatScalarAnswer(answer[key])
    }));
  }

  private answerFieldLabel(key: string): string {
    const labels: { [key: string]: string } = {
      response: 'Response',
      amount: 'Amount',
      percentage: 'Percentage',
      percent: 'Percent',
      frequency: 'Frequency',
      guideline: 'Guideline',
      guidelines: 'Guidelines',
      limitation: 'Limitation',
      age: 'Age',
      ageLowValue: 'Age From',
      ageHighValue: 'Age To',
      age_low_value: 'Age From',
      age_high_value: 'Age To',
      waitingPeriod: 'Waiting Period',
      waiting_period: 'Waiting Period',
      deductibleApplies: 'Deductible Applies',
      deductible_applies: 'Deductible Applies',
      maximumApplies: 'Maximum Applies',
      maximum_applies: 'Maximum Applies',
      disclaimers: 'Disclaimers',
      coverage: 'Coverage',
      eligible: 'Eligible',
      status: 'Status',
      type: 'Type'
    };

    if (labels[key]) {
      return labels[key];
    }

    return key
      .replace(/_/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, value => value.toUpperCase());
  }

  private formatScalarAnswer(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (typeof value === 'object') {
      return '-';
    }

    return String(value);
  }

  get sectionLabel(): string {
    if (!this.selectedQuestion) {
      return '-';
    }

    return this.sectionLabels[this.selectedQuestion.section] ||
      this.selectedQuestion.section;
  }

  get automaticFailureDetails(): Array<{ severity: string; count: number | null; limit: number | null }> {
    return this.automaticFailureSeverities.map(severity => ({
      severity,
      count: this.severityFailureCounts[severity] === undefined
        ? null
        : Number(this.severityFailureCounts[severity]),
      limit: this.severityFailureLimits[severity] === undefined
        ? null
        : Number(this.severityFailureLimits[severity])
    }));
  }

  formatDeduction(value: number | null): string {
    return value === null || value === undefined ? '-' : `${value}%`;
  }

  formatFailureThreshold(value: number | null): string {
    return value === null || value === undefined
      ? 'No automatic failure'
      : `${value} error${value === 1 ? '' : 's'}`;
  }

  getQuestionName(questionId: any): string {
    const id = Number(questionId);
    const question = this.questions.find(item => item.question_id === id);
    return question ? question.question : (questionId || '-');
  }

  displayValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    return typeof value === 'object'
      ? JSON.stringify(value)
      : String(value);
  }

  formatErrorType(value: string | null): string {
    if (!value) {
      return '-';
    }

    return value
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  formatStatus(value: string): string {
    return (value || '')
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  trackQuestion(index: number, question: AuditQuestion): number {
    return question.question_id;
  }

  private showApiError(title: string, error: any): void {
    const text =
      error?.error?.message ||
      error?.error?.detail ||
      error?.message ||
      'Something went wrong. Please try again.';

    swal.fire({
      icon: error?.status === 409 ? 'warning' : 'error',
      title,
      text
    });

    if (error?.status === 409) {
      this.loadAudit();
    }
  }
}
